<script setup>
/* global axios */
// Restrição de acesso da Função Personalizada: horário (por dia + intervalo) e IP.
// O ENFORCEMENT é 100% backend; aqui é só a configuração. v-model = objeto access_policy.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const props = defineProps({
  modelValue: { type: Object, required: true },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const route = useRoute();

const p = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});

const detectedIp = ref('');
const manualTz = ref(false);

// Lista INTERNACIONAL de fusos (IANA) via Intl; fallback curado se o browser não
// suportar supportedValuesOf.
const FALLBACK_TZ = [
  'UTC',
  'America/Sao_Paulo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Argentina/Buenos_Aires',
  'America/Santiago',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Moscow',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];
const allTimezones = (() => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (e) {
    // ignore
  }
  return FALLBACK_TZ;
})();
// garante que o valor atual (mesmo digitado à mão) apareça selecionado no dropdown
const timezones = computed(() => {
  const cur = p.value.timezone;
  return cur && !allTimezones.includes(cur)
    ? [cur, ...allTimezones]
    : allTimezones;
});

const DAYS = computed(() => [
  { k: '0', label: t('CUSTOM_ROLE.ACCESS.DAYS.SUN') },
  { k: '1', label: t('CUSTOM_ROLE.ACCESS.DAYS.MON') },
  { k: '2', label: t('CUSTOM_ROLE.ACCESS.DAYS.TUE') },
  { k: '3', label: t('CUSTOM_ROLE.ACCESS.DAYS.WED') },
  { k: '4', label: t('CUSTOM_ROLE.ACCESS.DAYS.THU') },
  { k: '5', label: t('CUSTOM_ROLE.ACCESS.DAYS.FRI') },
  { k: '6', label: t('CUSTOM_ROLE.ACCESS.DAYS.SAT') },
]);

const day = k => {
  const d = p.value.schedule.days[k] || { on: false };
  p.value.schedule.days[k] = d;
  return d;
};

const hasBreak = k => !!day(k).break;
const toggleBreak = k => {
  const d = day(k);
  d.break = d.break ? null : { start: '12:00', end: '13:00' };
};

// lista de IPs como texto (1 por linha) <-> array
const ipText = computed({
  get: () => (p.value.ip.allow || []).join('\n'),
  set: v => {
    p.value.ip.allow = v
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
  },
});

const loadIp = async () => {
  try {
    const base = `/api/v1/accounts/${route.params.accountId}/kanban/role_access_policies`;
    const { data } = await axios.get(`${base}/my_ip`);
    detectedIp.value = data?.data?.ip || '';
  } catch (e) {
    detectedIp.value = '';
  }
};
const useDetectedIp = () => {
  const cur = new Set(p.value.ip.allow || []);
  if (detectedIp.value) cur.add(detectedIp.value);
  p.value.ip.allow = [...cur];
};
onMounted(loadIp);
</script>

<template>
  <div class="pt-1">
    <label
      class="flex items-center gap-2 mb-1 text-sm font-medium cursor-pointer text-n-slate-12"
    >
      <input v-model="p.enabled" type="checkbox" />
      {{ t('CUSTOM_ROLE.ACCESS.TITLE') }}
    </label>
    <p class="mb-3 text-xs text-n-slate-10">{{ t('CUSTOM_ROLE.ACCESS.DESC') }}</p>

    <template v-if="p.enabled">
      <!-- fuso: dropdown internacional (IANA) + opção de digitar -->
      <div class="flex items-center justify-between mb-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CUSTOM_ROLE.ACCESS.TIMEZONE') }}
        </label>
        <label
          class="flex items-center gap-1 text-xs cursor-pointer text-n-slate-10"
        >
          <input v-model="manualTz" type="checkbox" />
          {{ t('CUSTOM_ROLE.ACCESS.TIMEZONE_MANUAL') }}
        </label>
      </div>
      <input
        v-if="manualTz"
        v-model="p.timezone"
        type="text"
        placeholder="Ex.: Europe/London"
        class="w-full mb-4 h-9 px-2 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
      />
      <select
        v-else
        v-model="p.timezone"
        class="w-full mb-4 h-9 px-2 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
      >
        <option v-for="tz in timezones" :key="tz" :value="tz">{{ tz }}</option>
      </select>

      <!-- horário -->
      <label
        class="flex items-center gap-2 mb-2 text-sm font-medium cursor-pointer text-n-slate-12"
      >
        <input v-model="p.schedule.enabled" type="checkbox" />
        {{ t('CUSTOM_ROLE.ACCESS.SCHEDULE') }}
      </label>
      <div v-if="p.schedule.enabled" class="flex flex-col gap-2 mb-4">
        <div
          v-for="d in DAYS"
          :key="d.k"
          class="flex flex-wrap items-center gap-2 p-2 border rounded-md border-n-weak"
        >
          <label class="flex items-center w-24 gap-2 text-sm cursor-pointer text-n-slate-12">
            <input v-model="day(d.k).on" type="checkbox" />
            {{ d.label }}
          </label>
          <template v-if="day(d.k).on">
            <input
              v-model="day(d.k).start"
              type="time"
              class="h-8 px-1 text-sm border rounded border-n-weak bg-n-alpha-1 text-n-slate-12"
            />
            <span class="text-n-slate-10">–</span>
            <input
              v-model="day(d.k).end"
              type="time"
              class="h-8 px-1 text-sm border rounded border-n-weak bg-n-alpha-1 text-n-slate-12"
            />
            <label class="flex items-center gap-1 ml-2 text-xs cursor-pointer text-n-slate-11">
              <input
                type="checkbox"
                :checked="hasBreak(d.k)"
                @change="toggleBreak(d.k)"
              />
              {{ t('CUSTOM_ROLE.ACCESS.BREAK') }}
            </label>
            <template v-if="hasBreak(d.k)">
              <input
                v-model="day(d.k).break.start"
                type="time"
                class="h-8 px-1 text-sm border rounded border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
              <span class="text-n-slate-10">–</span>
              <input
                v-model="day(d.k).break.end"
                type="time"
                class="h-8 px-1 text-sm border rounded border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </template>
          </template>
        </div>
      </div>

      <!-- IP -->
      <label
        class="flex items-center gap-2 mb-1 text-sm font-medium cursor-pointer text-n-slate-12"
      >
        <input v-model="p.ip.enabled" type="checkbox" />
        {{ t('CUSTOM_ROLE.ACCESS.IP') }}
      </label>
      <template v-if="p.ip.enabled">
        <p class="mb-1 text-xs text-n-slate-10">{{ t('CUSTOM_ROLE.ACCESS.IP_HINT') }}</p>
        <textarea
          v-model="ipText"
          rows="3"
          :placeholder="'203.0.113.10\n192.168.0.0/24'"
          class="w-full px-2 py-1 mb-1 text-sm border rounded-md resize-y border-n-weak bg-n-alpha-1 text-n-slate-12"
        />
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span>{{ t('CUSTOM_ROLE.ACCESS.DETECTED_IP') }}:</span>
          <span class="font-mono text-n-slate-12">{{ detectedIp || '—' }}</span>
          <button
            v-if="detectedIp"
            type="button"
            class="px-2 py-0.5 rounded bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3"
            @click="useDetectedIp"
          >
            {{ t('CUSTOM_ROLE.ACCESS.USE_DETECTED_IP') }}
          </button>
        </div>
      </template>
    </template>
  </div>
</template>
